/**
 * SEO multilingüe.
 *
 * El idioma visible para los buscadores viaja en el query param `?lang=en|pt`
 * (el español es el idioma por defecto, sin parámetro). Así el HTML renderizado
 * en el servidor —title, description, og:* y JSON-LD— ya sale traducido, algo
 * que la detección por navegador (client-side) no puede lograr.
 */

export type SeoLang = "es" | "en" | "pt";

export const SITE_URL = "https://dahianamarin.com";

export const SEO_LANGS: SeoLang[] = ["es", "en", "pt"];

export const OG_LOCALE: Record<SeoLang, string> = {
  es: "es_PY",
  en: "en_US",
  pt: "pt_BR",
};

export const HREFLANG: Record<SeoLang, string> = {
  es: "es-py",
  en: "en",
  pt: "pt-br",
};

export const IN_LANGUAGE: Record<SeoLang, string> = {
  es: "es-PY",
  en: "en-US",
  pt: "pt-BR",
};

/** Lee el idioma desde los search params (server-safe). */
export function pickLang(search: unknown): SeoLang {
  const raw =
    search && typeof search === "object"
      ? (search as Record<string, unknown>)["lang"]
      : undefined;
  return raw === "en" || raw === "pt" ? raw : "es";
}

/** URL absoluta de una ruta en un idioma dado. */
export function langUrl(path: string, lang: SeoLang) {
  const clean = path === "/" ? "/" : path.replace(/\/+$/, "");
  return lang === "es" ? `${SITE_URL}${clean}` : `${SITE_URL}${clean}?lang=${lang}`;
}

type HeadInput = {
  lang: SeoLang;
  path: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  image?: string;
  /** Si false, no se emiten alternates (páginas cuyo contenido solo existe en español). */
  alternates?: boolean;
};

/** Construye meta + links (canonical y hreflang) para una página pública. */
export function buildHead({
  lang,
  path,
  title,
  description,
  ogTitle,
  ogDescription,
  ogType = "website",
  image,
  alternates = true,
}: HeadInput) {
  const url = langUrl(path, lang);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: ogTitle ?? title },
    { property: "og:description", content: ogDescription ?? description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: url },
    { property: "og:locale", content: OG_LOCALE[lang] },
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: ogTitle ?? title },
    { name: "twitter:description", content: ogDescription ?? description },
  ];

  if (image) {
    const absImage = image.startsWith("http") ? image : `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
    meta.push({ property: "og:image", content: absImage });
    meta.push({ name: "twitter:image", content: absImage });
  }

  const links: Array<Record<string, string>> = [{ rel: "canonical", href: url }];

  if (alternates) {
    for (const l of SEO_LANGS) {
      links.push({ rel: "alternate", hrefLang: HREFLANG[l], href: langUrl(path, l) });
    }
    links.push({ rel: "alternate", hrefLang: "x-default", href: langUrl(path, "es") });
  }

  return { meta, links, url };
}

/* ------------------------------------------------------------------ */
/* Diccionarios SEO por página                                         */
/* ------------------------------------------------------------------ */

type Copy = { title: string; description: string; ogTitle?: string; ogDescription?: string };

export const PAGE_SEO: Record<string, Record<SeoLang, Copy>> = {
  home: {
    es: {
      title: "Dahiana Marín Bienes Raíces — Propiedades en Paraguay",
      description:
        "Encontrá casas, departamentos y terrenos en venta o alquiler en Asunción, Villa Morra, Recoleta, Las Lomas y todo Central. Asesoría personalizada.",
      ogTitle: "Dahiana Marín Bienes Raíces",
      ogDescription: "Portal inmobiliario en Paraguay.",
    },
    en: {
      title: "Dahiana Marín Real Estate — Property in Paraguay",
      description:
        "Houses, apartments and land for sale or rent in Asunción, Villa Morra, Recoleta, Las Lomas and greater Central. Personal guidance for foreign buyers.",
      ogTitle: "Dahiana Marín Real Estate",
      ogDescription: "Real estate portal in Paraguay.",
    },
    pt: {
      title: "Dahiana Marín Imóveis — Imóveis no Paraguai",
      description:
        "Casas, apartamentos e terrenos à venda ou para alugar em Assunção, Villa Morra, Recoleta, Las Lomas e todo o Central. Assessoria para brasileiros.",
      ogTitle: "Dahiana Marín Imóveis",
      ogDescription: "Portal imobiliário no Paraguai.",
    },
  },
  agente: {
    es: {
      title: "Sobre Dahiana Marín — Asesora inmobiliaria en Paraguay",
      description:
        "Conocé a Dahiana Marín, asesora inmobiliaria con foco en Asunción y Central. Acompañamiento personalizado en compra, venta y alquiler.",
      ogTitle: "Dahiana Marín — Asesora inmobiliaria",
      ogDescription: "Marca personal inmobiliaria en Paraguay.",
    },
    en: {
      title: "About Dahiana Marín — Real estate agent in Paraguay",
      description:
        "Meet Dahiana Marín, a real estate advisor focused on Asunción and Central. Personal support for buying, selling and renting, including foreign buyers.",
      ogTitle: "Dahiana Marín — Real estate agent",
      ogDescription: "Personal real estate advisory in Paraguay.",
    },
    pt: {
      title: "Sobre Dahiana Marín — Corretora de imóveis no Paraguai",
      description:
        "Conheça Dahiana Marín, corretora de imóveis com foco em Assunção e Central. Acompanhamento personalizado na compra, venda e aluguel, inclusive para brasileiros.",
      ogTitle: "Dahiana Marín — Corretora de imóveis",
      ogDescription: "Assessoria imobiliária personalizada no Paraguai.",
    },
  },
  blog: {
    es: {
      title: "Blog inmobiliario — Dahiana Marín Bienes Raíces",
      description:
        "Tips, análisis de mercado y guías para comprar, vender o alquilar en Paraguay.",
      ogTitle: "Blog inmobiliario — Dahiana Marín",
      ogDescription: "Guías, tips y análisis del mercado en Paraguay.",
    },
    en: {
      title: "Real estate blog — Dahiana Marín Real Estate",
      description:
        "Guides, market analysis and practical tips for buying, selling or renting property in Paraguay.",
      ogTitle: "Real estate blog — Dahiana Marín",
      ogDescription: "Guides, tips and market analysis in Paraguay.",
    },
    pt: {
      title: "Blog imobiliário — Dahiana Marín Imóveis",
      description:
        "Guias, análises de mercado e dicas práticas para comprar, vender ou alugar imóveis no Paraguai.",
      ogTitle: "Blog imobiliário — Dahiana Marín",
      ogDescription: "Guias, dicas e análise de mercado no Paraguai.",
    },
  },
  proyectos: {
    es: {
      title: "Proyectos y emprendimientos en Asunción | Dahiana Marín",
      description:
        "Emprendimientos y proyectos en pozo en Asunción y Central: departamentos en construcción, entrega a plazo y planes de pago. Consultá disponibilidad con Dahiana Marín.",
      ogTitle: "Proyectos y emprendimientos en Asunción",
    },
    en: {
      title: "New developments in Asunción | Dahiana Marín",
      description:
        "Off-plan and under-construction developments in Asunción and Central: payment plans, delivery schedules and availability. Ask Dahiana Marín for current units.",
      ogTitle: "New developments in Asunción",
    },
    pt: {
      title: "Lançamentos e empreendimentos em Assunção | Dahiana Marín",
      description:
        "Empreendimentos na planta e em construção em Assunção e Central: planos de pagamento, prazos de entrega e disponibilidade. Consulte com Dahiana Marín.",
      ogTitle: "Lançamentos e empreendimentos em Assunção",
    },
  },
  vender: {
    es: {
      title: "Vendé tu propiedad en Paraguay | Dahiana Marín",
      description:
        "Tasación gratuita, fotos profesionales y difusión en portales. Publicá tu casa, departamento o terreno con Dahiana Marín.",
      ogTitle: "Vendé tu propiedad con Dahiana Marín",
    },
    en: {
      title: "Sell your property in Paraguay | Dahiana Marín",
      description:
        "Free valuation, professional photography and portal exposure. List your house, apartment or land with Dahiana Marín.",
      ogTitle: "Sell your property with Dahiana Marín",
    },
    pt: {
      title: "Venda seu imóvel no Paraguai | Dahiana Marín",
      description:
        "Avaliação gratuita, fotos profissionais e divulgação em portais. Anuncie sua casa, apartamento ou terreno com Dahiana Marín.",
      ogTitle: "Venda seu imóvel com Dahiana Marín",
    },
  },
};

/* Etiquetas de operación y tipo para las rutas de listado ------------ */

export const OPERATION_SEO: Record<SeoLang, Record<string, string>> = {
  es: { venta: "Venta", alquiler: "Alquiler", alquiler_temporal: "Alquiler temporal" },
  en: { venta: "For sale", alquiler: "For rent", alquiler_temporal: "Short-term rentals" },
  pt: { venta: "À venda", alquiler: "Para alugar", alquiler_temporal: "Aluguel por temporada" },
};

export const TYPE_SEO: Record<SeoLang, Record<string, string>> = {
  es: {
    casas: "casas",
    departamentos: "departamentos",
    terrenos: "terrenos",
    oficinas: "oficinas",
    locales: "locales comerciales",
    duplex: "dúplex",
  },
  en: {
    casas: "houses",
    departamentos: "apartments",
    terrenos: "land",
    oficinas: "offices",
    locales: "retail spaces",
    duplex: "duplexes",
  },
  pt: {
    casas: "casas",
    departamentos: "apartamentos",
    terrenos: "terrenos",
    oficinas: "escritórios",
    locales: "lojas",
    duplex: "duplex",
  },
};

export function humanizeSlug(slug: string) {
  return slug
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

/** Copy de la página de listado (con o sin ubicación). */
export function listingCopy(
  lang: SeoLang,
  operation: string,
  type: string,
  location?: string,
): { h1: string; title: string; description: string; intro: string } {
  const op = OPERATION_SEO[lang][operation] ?? operation;
  const t = TYPE_SEO[lang][type] ?? type;
  const place = location ? humanizeSlug(location) : lang === "en" ? "Paraguay" : "Paraguay";

  if (lang === "en") {
    const h1 = `${t.charAt(0).toUpperCase() + t.slice(1)} ${op.toLowerCase()} in ${place}`;
    return {
      h1,
      title: `${h1} | Dahiana Marín`,
      description: `${t.charAt(0).toUpperCase() + t.slice(1)} ${op.toLowerCase()} in ${place}: updated prices, sizes and availability. Curated listings with English-speaking guidance for foreign buyers.`,
      intro: `A curated selection by Dahiana Marín — ${t} available ${op.toLowerCase()} in ${place}, with title verification and step-by-step support for buyers abroad.`,
    };
  }

  if (lang === "pt") {
    const where = place === "Paraguay" ? "no Paraguai" : `em ${place}`;
    const h1 = `${t.charAt(0).toUpperCase() + t.slice(1)} ${op.toLowerCase()} ${where}`;
    return {
      h1,
      title: `${h1} | Dahiana Marín`,
      description: `${h1}: preços, metragens e disponibilidade atualizados. Imóveis selecionados por Dahiana Marín, com atendimento em português para brasileiros.`,
      intro: `Seleção de Dahiana Marín — ${t} ${op.toLowerCase()} ${where}, com verificação de documentação e acompanhamento para compradores brasileiros.`,
    };
  }

  const h1 = `${op} de ${t} en ${place}`;
  return {
    h1,
    title: `${h1} | Dahiana Marín`,
    description: `${h1}: precios, superficies y disponibilidad actualizada. Cartera curada por Dahiana Marín, con asesoría personalizada y verificación de títulos.`,
    intro: `Cartera curada por Dahiana Marín — ${t} seleccionadas para ${op.toLowerCase()} en ${place}.`,
  };
}

/* ------------------------------------------------------------------ */
/* Contenido traducido almacenado en la base de datos                  */
/* ------------------------------------------------------------------ */

/**
 * Devuelve el valor de un campo en el idioma pedido, con fallback al español.
 * Ej: localized(property, "title", "en") → title_en ?? title
 */
export function localized<T extends Record<string, unknown>>(
  row: T | null | undefined,
  field: string,
  lang: SeoLang,
): string | null {
  if (!row) return null;
  const base = (row[field] as string | null | undefined) ?? null;
  if (lang === "es") return base;
  const translated = (row[`${field}_${lang}`] as string | null | undefined) ?? null;
  return translated && translated.trim() ? translated : base;
}
